/**
 * cartLabel — ONE place that decides what a cart is called.
 *
 * Home, New Acct and the TopBar Resume chip all name the same cart, and they
 * used to disagree: New Acct showed "Application — Sample Adult Test" while
 * Home showed "No accounts yet · acct id gen" and the chip showed
 * "Resume cart · 9d3c2df4". Three surfaces, three answers, one cart.
 *
 * ENVELOPE BLINDNESS — the bug underneath, and the second time this repo has
 * hit it. The canonical v3 blob is an ENVELOPE, `{ "version": 1, "people": [] }`,
 * not a bare array, so `Array.isArray(parsed) ? parsed : []` discarded every
 * real cart. The server hit exactly this on 2026-06-15
 * (LnVerificationGateService.TryGetPeopleArray: "Tolerate BOTH the canonical v3
 * envelope and a bare array — envelope blindness blocked commit") and fixed it
 * there; the client copy never got the fix, so a July change to the FALLBACK
 * wording left the parser blind and the symptom returned unchanged.
 */

/** Accepts a bare array OR the `{version, <key>: []}` envelope. */
export function unwrapCollection(raw: string | null | undefined, key: string): unknown[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) return parsed;
    if (parsed && typeof parsed === 'object') {
      const inner = (parsed as Record<string, unknown>)[key];
      if (Array.isArray(inner)) return inner;
    }
    return [];
  } catch {
    return [];
  }
}

function readString(o: Record<string, unknown>, k: string): string {
  return typeof o[k] === 'string' ? (o[k] as string).trim() : '';
}

/** Display name for one person entry, tolerant of the field names in use. */
export function personName(p: unknown): string {
  const o = (p ?? {}) as Record<string, unknown>;
  // Composed parts first (First Middle Last Suffix - Bill 2026-09-11 ruling);
  // a stored displayName/fullName was usually built first+last by older code.
  const composed = [
    readString(o, 'firstName') || readString(o, 'first'),
    readString(o, 'middleName') || readString(o, 'middleInitial'),
    readString(o, 'lastName') || readString(o, 'last'),
    readString(o, 'suffix'),
  ].filter(Boolean).join(' ');
  if (composed) return composed;
  return readString(o, 'displayName') || readString(o, 'fullName') || readString(o, 'name');
}

export interface CartIdentity {
  who?: string;
  what?: string;
  others: number;
}

/** Optional code -> display-name resolver (the config product catalog). */
export type ProductNameResolver = (code: string) => string | undefined;

export function identifyCart(
  peopleJson: string | null | undefined,
  productsJson: string | null | undefined,
  resolveProduct?: ProductNameResolver,
): CartIdentity {
  let who: string | undefined;
  let what: string | undefined;
  let others = 0;

  const named = unwrapCollection(peopleJson, 'people')
    .map(personName)
    .filter((n) => n.length > 0);
  if (named.length > 0) {
    who = named[0];
    others = named.length - 1;
  }

  const products = unwrapCollection(productsJson, 'products');
  if (products.length > 0) {
    const first = (products[0] ?? {}) as Record<string, unknown>;
    // A cart's product entry carries the CODE (e.g. STUDEN_MINOR); the human
    // name lives in the product catalog. New Acct resolves it that way and
    // showed "Minor My Student Checking" while Home showed the raw code
    // (Bill 2026-09-11) - so resolve through the same catalog here.
    const code = readString(first, 'code') || readString(first, 'productCode');
    const label =
      readString(first, 'displayName') ||
      readString(first, 'name') ||
      readString(first, 'productName') ||
      (code && resolveProduct?.(code)) ||
      code;
    if (label) what = products.length > 1 ? `${label} +${products.length - 1}` : label;
  }

  return { ...(who ? { who } : {}), ...(what ? { what } : {}), others };
}

/** The one-line title: "Sample Adult Test +1 · Minor My Student Checking". */
export function cartTitle(id: CartIdentity): string | undefined {
  if (!id.who) return undefined;
  const lead = id.others > 0 ? `${id.who} +${id.others}` : id.who;
  return [lead, id.what].filter(Boolean).join(' · ');
}
